import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation } from 'react-query';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Badge, { BadgeVariants } from '@/components/layout/Badge';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import { mdiLoading, mdiPlus, mdiSync, mdiTrashCan } from '@mdi/js';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { queryClient } from '@/queryClient';
import { FeedDTO, FeedStatus } from '@webip/shared/src/dto/firewall';

type ResponseFeeds = {
    ok?: boolean;
    result: FeedDTO[];
};

const STATUS_VARIANT: Record<FeedStatus, BadgeVariants> = {
    ok: 'success',
    error: 'danger',
    syncing: 'warning',
};

export function FeedsTab() {
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [newFeed, setNewFeed] = useState({ name: '', url: '' });
    const [syncingId, setSyncingId] = useState<number | null>(null);

    const queryFeeds = useQuery({
        queryKey: ['fw_feeds'],
        queryFn: async () =>
            axios.get<ResponseFeeds>('/api/firewall/feeds', { withCredentials: true }),
        refetchOnWindowFocus: false,
        refetchInterval: (data) => {
            const feeds = data?.data?.result ?? [];
            return feeds.some((f: FeedDTO) => f.status === 'syncing') ? 3000 : false;
        },
    });

    const mutAdd = useMutation(
        async (data: { name: string; url: string }) =>
            axios.post('/api/firewall/feeds', data, { withCredentials: true }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_feeds']);
                toastSuccess('Feed adicionado com sucesso!');
                setIsAddOpen(false);
                setNewFeed({ name: '', url: '' });
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao adicionar feed');
                toastError(message);
            },
        },
    );

    const mutDelete = useMutation(
        async (id: number) =>
            axios.delete(`/api/firewall/feeds/${id}`, { withCredentials: true }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_feeds']);
                toastSuccess('Feed removido com sucesso!');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao remover feed');
                toastError(message);
            },
        },
    );

    const handleSync = useCallback(async (id: number) => {
        if (syncingId) return;
        setSyncingId(id);
        try {
            await axios.post(`/api/firewall/feeds/${id}/sync`, {}, { withCredentials: true });
            queryClient.invalidateQueries(['fw_feeds']);
            toastSuccess('Sincronização iniciada!');
        } catch (error) {
            const { message } = extractApiError(error, 'Erro ao sincronizar feed');
            toastError(message);
        } finally {
            setSyncingId(null);
        }
    }, [syncingId]);

    const feeds = queryFeeds.data?.data?.result ?? [];

    const headers: DataTableHeader[] = [
        {
            label: 'Nome',
            column: 'name',
            width: '25%',
        },
        {
            label: 'URL',
            column: 'url',
            width: '35%',
        },
        {
            label: 'Entradas',
            width: '100px',
            alignCenter: true,
            render: (row: FeedDTO) => row.entries.toLocaleString('pt-BR'),
        },
        {
            label: 'Status',
            width: '100px',
            alignCenter: true,
            render: (row: FeedDTO) => (
                <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
            ),
        },
        {
            label: 'Último sync',
            width: '160px',
            render: (row: FeedDTO) =>
                row.lastSync
                    ? new Date(row.lastSync).toLocaleString('pt-BR')
                    : '—',
        },
        {
            label: 'Ações',
            width: '100px',
            stickRight: true,
            alignRight: true,
            render: (row: FeedDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="outline-success"
                        icon={syncingId === row.id ? mdiLoading : mdiSync}
                        iconProps={{ spin: syncingId === row.id }}
                        disabled={syncingId === row.id || row.status === 'syncing'}
                        onClick={() => handleSync(row.id)}
                        title="Sincronizar feed"
                    />
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        icon={mdiTrashCan}
                        onConfirm={() => mutDelete.mutate(row.id)}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <VBox style={{ gap: '16px', flex: 1, overflow: 'hidden' }}>
            <HBox style={{ justifyContent: 'flex-end' }}>
                <Botao
                    variant="primary"
                    icon={mdiPlus}
                    onClick={() => setIsAddOpen(true)}
                >
                    Adicionar Feed
                </Botao>
            </HBox>

            {queryFeeds.isLoading && (
                <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                    <Icon path={mdiLoading} spin size={2} />
                </HBox>
            )}

            {queryFeeds.isError && (
                <p style={{ color: 'var(--color-danger)' }}>Erro ao carregar feeds. Tente novamente.</p>
            )}

            {!queryFeeds.isLoading && !queryFeeds.isError && (
                <DataTable
                    cabecalhos={headers}
                    linhas={feeds}
                />
            )}

            <Modal
                show={isAddOpen}
                title="Adicionar Feed de IP"
                onClose={() => setIsAddOpen(false)}
                onCancel={() => setIsAddOpen(false)}
                onConfirm={() => newFeed.name.trim() && newFeed.url.trim() && mutAdd.mutate(newFeed)}
                confirmProps={{
                    label: 'Adicionar',
                    disabled: mutAdd.isLoading || !newFeed.name.trim() || !newFeed.url.trim(),
                }}
                cancelProps={{ label: 'Cancelar' }}
                size="sm"
            >
                <VBox style={{ gap: '12px' }}>
                    <VBox>
                        <label style={{ fontWeight: 500, marginBottom: 4 }}>Nome</label>
                        <input
                            className="form-control"
                            value={newFeed.name}
                            onChange={e => setNewFeed(f => ({ ...f, name: e.target.value }))}
                            placeholder="Ex: Blocklist Malware"
                            autoFocus
                        />
                    </VBox>
                    <VBox>
                        <label style={{ fontWeight: 500, marginBottom: 4 }}>URL do feed</label>
                        <input
                            className="form-control"
                            value={newFeed.url}
                            onChange={e => setNewFeed(f => ({ ...f, url: e.target.value }))}
                            placeholder="https://..."
                        />
                    </VBox>
                </VBox>
            </Modal>
        </VBox>
    );
}
